/**
 * Two jobs, both belonging to the cart drawer (docs/cart-flow.md):
 * swipe-down-to-dismiss for the mobile sheet, and the header cart button —
 * see the second section at the bottom of this file.
 *
 * Swipe-down-to-dismiss for the mobile cart sheet (assets/ag-cart.css).
 *
 * The reason the sheet exists at all is that leaving it has to be cheap — a
 * drawer people feel trapped in is the one that costs mobile conversions. The
 * close button and the backdrop already work; this adds the gesture people
 * actually reach for.
 *
 * Opening, closing, focus and history stay with `<theme-drawer>`. This only
 * tracks the finger and, past the threshold, asks the drawer to close. The
 * drag offset is published as `--ag-sheet-y` so Horizon's own closing
 * animation can start from wherever the sheet was let go instead of snapping
 * back to the top first.
 */

const MOBILE = '(max-width: 749px)';
const DISTANCE_THRESHOLD = 90; // px dragged before a release closes the sheet
const FLICK_DISTANCE = 40; // px, when thrown fast enough
const FLICK_VELOCITY = 0.5; // px per ms

/** @type {{ dialog: HTMLDialogElement, drawer: Element, startY: number, prevY: number, prevT: number, velocity: number, offset: number, active: boolean } | null} */
let drag = null;

const isMobile = () => window.matchMedia(MOBILE).matches;

/**
 * True when nothing between `from` and `dialog` is scrolled away from the top.
 * Dragging the sheet while the item list is mid-scroll would fight the scroll.
 * @param {Element | null} from
 * @param {Element} dialog
 */
function atScrollTop(from, dialog) {
  let node = from;
  while (node && node !== dialog) {
    if (node instanceof HTMLElement && node.scrollHeight > node.clientHeight + 1 && node.scrollTop > 0) {
      return false;
    }
    node = node.parentElement;
  }
  return true;
}

/** @param {HTMLDialogElement} dialog @param {number} y */
function setOffset(dialog, y) {
  dialog.style.setProperty('--ag-sheet-y', `${y}px`);
  dialog.style.transform = y ? `translateY(${y}px)` : '';
}

/** @param {HTMLDialogElement} dialog */
function clearOffset(dialog) {
  dialog.style.removeProperty('--ag-sheet-y');
  dialog.style.transform = '';
  dialog.classList.remove('ag-sheet-dragging', 'ag-sheet-settling');
}

/** @param {TouchEvent} event */
function onTouchStart(event) {
  if (drag || !isMobile() || event.touches.length !== 1) return;

  const target = event.target instanceof Element ? event.target : null;
  const dialog = target?.closest('#cart-drawer dialog');
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open) return;

  // A finger on a control is aiming at the control, not at the sheet.
  if (target?.closest('button, a, input, textarea, select, [role="button"]')) return;
  if (!atScrollTop(target, dialog)) return;

  const drawer = dialog.closest('theme-drawer');
  if (!drawer) return;

  // The move handler has to be non-passive (it calls preventDefault so the
  // page does not scroll under the sheet), and a non-passive touchmove on the
  // document makes every scroll on every page wait for it. So it is attached
  // to the dialog only while a drag is armed, and removed in endDrag.
  dialog.addEventListener('touchmove', onTouchMove, { passive: false });

  const touch = event.touches[0];
  drag = {
    dialog,
    drawer,
    startY: touch.clientY,
    prevY: touch.clientY,
    prevT: event.timeStamp,
    velocity: 0,
    offset: 0,
    active: false,
  };
}

/** @param {TouchEvent} event */
function onTouchMove(event) {
  if (!drag || event.touches.length !== 1) return;

  const touch = event.touches[0];
  const dy = touch.clientY - drag.startY;

  // An upward move means they meant to scroll the list — hand the gesture back.
  if (!drag.active && dy < 4) {
    if (dy < -4) endDrag(false);
    return;
  }

  if (!drag.active) {
    drag.active = true;
    drag.dialog.classList.add('ag-sheet-dragging');
    drag.dialog.classList.remove('ag-sheet-settling');
  }

  // Resistance past the top edge so the sheet never lifts off its anchor.
  drag.offset = dy > 0 ? dy : dy / 4;
  if (drag.offset < 0) drag.offset = 0;

  // Speed of this move alone — a slow drag that happens to have travelled far
  // should not read as a flick, and vice versa.
  const elapsed = event.timeStamp - drag.prevT;
  if (elapsed > 0) drag.velocity = (touch.clientY - drag.prevY) / elapsed;
  drag.prevY = touch.clientY;
  drag.prevT = event.timeStamp;

  if (event.cancelable) event.preventDefault();
  setOffset(drag.dialog, drag.offset);
}

/** @param {boolean} settle */
function endDrag(settle) {
  if (!drag) return;
  const { dialog, drawer, offset, active, velocity } = drag;
  drag = null;
  dialog.removeEventListener('touchmove', onTouchMove);

  if (!active) {
    clearOffset(dialog);
    return;
  }

  dialog.classList.remove('ag-sheet-dragging');

  const shouldClose =
    settle && (offset > DISTANCE_THRESHOLD || (offset > FLICK_DISTANCE && velocity > FLICK_VELOCITY));

  if (shouldClose) {
    // --ag-sheet-y is left in place: the closing keyframes read it so the
    // sheet carries on down from here. It is cleared on the next open.
    dialog.style.transform = '';
    /** @type {any} */ (drawer).close?.();
    return;
  }

  dialog.classList.add('ag-sheet-settling');
  setOffset(dialog, 0);
  dialog.addEventListener(
    'transitionend',
    () => dialog.classList.remove('ag-sheet-settling'),
    { once: true }
  );
}

document.addEventListener('touchstart', onTouchStart, { passive: true });
document.addEventListener('touchend', () => endDrag(true), { passive: true });
document.addEventListener('touchcancel', () => endDrag(false), { passive: true });

// Every open starts from a clean sheet, whatever the last gesture left behind.
document.addEventListener('theme-drawer:open', (event) => {
  const drawer = event.target;
  if (!(drawer instanceof Element) || drawer.id !== 'cart-drawer') return;
  const dialog = drawer.querySelector('dialog');
  if (dialog instanceof HTMLDialogElement) clearOffset(dialog);
});

/* ------------------------------------------------ header cart button ------
 *
 * The header cart stays an `<a href="/cart">` in the markup (see
 * sections/ag-header.liquid) and is upgraded here into a drawer trigger. A
 * `<button>` — which is what Horizon's own header-actions uses — would have
 * been less code, but it throws away middle-click, cmd-click and the no-JS
 * path, and /cart is a real destination people arrive at from ads and the
 * back button. So: plain left click opens the drawer, everything else stays a
 * link.
 *
 * The order below matters. preventDefault() is only called once the drawer is
 * confirmed to exist AND to be upgraded far enough to have toggle(); if it is
 * not, the click is left alone and the browser navigates to /cart. Swallowing
 * it would leave a shopper on a slow connection with a cart button that does
 * nothing at all.
 */

const CART_LINES_UPDATE = 'shopify:cart:lines-update'; // StandardEvents.cartLinesUpdate

/* ag-cart.js is loaded in <head> before snippets/scripts.liquid renders the
   import map, so it cannot use bare specifiers — hence the literal above
   rather than an import from '@shopify/events'. */

/** @returns {(Element & { toggle?: () => void }) | null} */
function resolveCartDrawer() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return null;

  // Close the custom-element upgrade race the same way component.js does:
  // the element can be in the DOM before theme-drawer.js has run.
  if (typeof (/** @type {any} */ (drawer).toggle) !== 'function') {
    customElements.upgrade(drawer);
  }

  return typeof (/** @type {any} */ (drawer).toggle) === 'function' ? drawer : null;
}

/** @param {MouseEvent} event */
function onCartTriggerClick(event) {
  // Anything but a plain left click is someone asking for the URL itself.
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (event.defaultPrevented) return;

  const target = event.target instanceof Element ? event.target.closest('[data-ag-cart-trigger]') : null;
  if (!target) return;

  const drawer = resolveCartDrawer();
  if (!drawer) return; // let the link do its job

  event.preventDefault();
  /** @type {any} */ (drawer).toggle();
}

document.addEventListener('click', onCartTriggerClick);

/**
 * A Studio page, with or without the market's locale root (`/en-pl/apps/studio/…`).
 * @param {string} pathname
 */
function isStudioPath(pathname) {
  const root = /** @type {any} */ (window).Shopify?.routes?.root;
  const localized = typeof root === 'string' && root.startsWith('/') && !root.startsWith('//')
    ? `${root.replace(/\/+$/, '')}/apps/studio/`
    : '/apps/studio/';
  return pathname.startsWith('/apps/studio/') || pathname.startsWith(localized);
}

// Horizon restores open drawers after navigation. Clear that state through its
// public close contract so a saved map opens in view, without a cart over it.
document.addEventListener('click', (event) => {
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (event.defaultPrevented) return;
  const link = event.target instanceof Element ? event.target.closest('#cart-drawer a[href]') : null;
  if (!(link instanceof HTMLAnchorElement) || link.hasAttribute('download')) return;
  if (link.target && link.target !== '_self') return;
  if (link.origin !== window.location.origin || !isStudioPath(link.pathname)) return;

  const drawer = document.getElementById('cart-drawer');
  if (typeof (/** @type {any} */ (drawer)?.close) === 'function') {
    /** @type {any} */ (drawer).close();
  }
}, { passive: true });

/* The trigger only promises a dialog once we know we can deliver one, so the
   ARIA is written from here rather than baked into the Liquid. */
function describeTriggers() {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  // The drawer can already be open on load — theme-drawer's restore path sets
  // [open] before its module runs — so read the state rather than assuming it.
  const expanded = String(drawer.hasAttribute('open'));

  for (const el of document.querySelectorAll('[data-ag-cart-trigger]')) {
    el.setAttribute('aria-haspopup', 'dialog');
    el.setAttribute('aria-controls', 'cart-drawer');
    el.setAttribute('aria-expanded', expanded);
  }
}

/** @param {boolean} expanded */
function reflectExpanded(expanded) {
  for (const el of document.querySelectorAll('[data-ag-cart-trigger][aria-haspopup]')) {
    el.setAttribute('aria-expanded', String(expanded));
  }
}

document.addEventListener('theme-drawer:open', (event) => {
  if (event.target instanceof Element && event.target.id === 'cart-drawer') reflectExpanded(true);
});

document.addEventListener('theme-drawer:close', (event) => {
  if (event.target instanceof Element && event.target.id === 'cart-drawer') reflectExpanded(false);
});

/* The header count was only ever written by the add-to-cart flows in
   ag-product.js and ag-gift-card.js. That was survivable while the button was
   a link — you left the page and the count came back server-rendered. Now that
   the drawer opens in place, removing the last print from it would leave the
   header still claiming a cart, so the count follows every cart change. */
function syncCartCount(count) {
  if (typeof count !== 'number') return;
  for (const el of document.querySelectorAll('[data-ag-cart-count]')) {
    el.textContent = String(count);
    /* On a phone the count is a badge on the bag rather than "Cart (0)", and
       an empty cart should not wear one. CSS cannot test a number, so the
       state is written here. */
    el.toggleAttribute('data-empty', count === 0);
  }
}

document.addEventListener(CART_LINES_UPDATE, (event) => {
  const promise = /** @type {any} */ (event).promise ?? /** @type {any} */ (event).detail?.promise;

  Promise.resolve(promise)
    .then((result) => {
      const count = result?.cart?.totalQuantity ?? /** @type {any} */ (event).detail?.itemCount;
      syncCartCount(typeof count === 'number' ? count : undefined);
    })
    .catch(() => {
      /* an add that failed leaves the count as it was, which is correct */
    });
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', describeTriggers, { once: true });
} else {
  describeTriggers();
}
